import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
} from '@material-ui/core';
import { Row, Col } from 'react-flexbox-grid';
import './registration.scss';
import config from '../../config';
import { serializeQuery, handleResponse } from '../../utils/handleResponse';
import Footer from '../../components/Footer';
import Header from '../Header';
import Loading from '../../components/loading';
import { GENDERS, SHOES } from '../../utils/utils';

const RegistrationPage = () => {
  const [countries, setCountries] = useState([]);
  const [userExist, setUserExist] = useState(false);
  const [isCaptain, setIsCaptain] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    birthdate: '',
    identificationCard: '',
    address: '',
    countryId: '',
    stateId: '',
    cityId: '',
    stravaId: '',
    refreshToken: '',
    cityName: '',
    countryName: '',
    photoUrl: '',
    shoesBrand: '',
  });

  const [errors, setErrors] = useState({});

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateFields = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = 'El nombre es obligatorio.';
    if (!formData.lastName) newErrors.lastName = 'El apellido es obligatorio.';
    if (!formData.email) newErrors.email = 'El email es obligatorio.';
    if (!isValidEmail(formData.email))
      newErrors.email = 'El email no es válido.';
    if (!formData.phone) newErrors.phone = 'El teléfono es obligatorio.';
    if (!formData.gender) newErrors.gender = 'El género es obligatorio.';
    if (!formData.birthdate)
      newErrors.birthdate = 'La fecha de nacimiento es obligatoria.';
    if (!formData.identificationCard)
      newErrors.identificationCard = 'La cédula es obligatoria.';
    if (!formData.address) newErrors.address = 'La dirección es obligatoria.';
    if (!formData.countryId) newErrors.countryId = 'El país es obligatorio.';
    if (!formData.stateId) newErrors.stateId = 'El estado es obligatorio.';
    if (!formData.cityId) newErrors.cityId = 'La ciudad es obligatoria.';
    if (!formData.shoesBrand) {
      newErrors.shoesBrand = 'La marca de zapatos es obligatoria.';
    }
    console.log('newErrors', newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[field];
      return newErrors;
    });
  };

  const getAccesToken = async (refreshToken) => {
    setShowLoading(true);
    const url = new URL(`https://www.strava.com/oauth/token`);
    const params = {
      client_id: config.clientID,
      client_secret: config.clientSecret,
      refresh_token: refreshToken,
      grant_type: 'refresh_token',
    };
    const requestOptions = {
      method: 'POST',
    };
    url.search = serializeQuery(params);
    const response = await fetch(url, requestOptions);
    setShowLoading(false);
    try {
      const body = await handleResponse(response);
      const refreshToken = body.refresh_token;

      setFormData({
        ...formData,
        refreshToken,
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchCountries = async () => {
    setShowLoading(true);
    try {
      const response = await fetch(`${config.tncApiUrl}/countries`);
      const data = await response.json();
      setCountries(data.data.body);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setShowLoading(false);
    }
  };

  const fetchStates = async (countryId) => {
    setShowLoading(true);
    try {
      const response = await fetch(
        `${config.tncApiUrl}/countries/${countryId}/states`
      );
      const data = await response.json();
      setStates(data.data.body);
    } catch (error) {
      console.error('Error fetching states:', error);
    } finally {
      setShowLoading(false);
    }
  };

  const fetchCities = async (stateId) => {
    setShowLoading(true);
    try {
      const response = await fetch(
        `${config.tncApiUrl}/states/${stateId}/cities`
      );
      const data = await response.json();
      setCities(data.data.body);
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setShowLoading(false);
    }
  };

  const loginStrava = async (userData, stravaId) => {
    setShowLoading(true);
    try {
      const payload = {
        stravaId,
      };
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      };
      const response = await fetch(
        `${config.tncApiUrl}/user/login`,
        requestOptions
      );

      if (!response.ok) {
        setShowLoading(false);
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error en el registro del evento');
      }

      const result = await response.json();
      setShowLoading(false);
      localStorage.setItem(
        'userData',
        JSON.stringify({ ...userData, userToken: result.data.accessToken })
      );
      window.location.replace('/');
    } catch (error) {
      setShowLoading(false);
      console.error('Error en la solicitud de registro:', error);
      alert(error.message || 'Error al generar el pago');
    }
  };

  const userRegistrationStatusEmail = async (debouncedEmail) => {
    setShowLoading(true);

    try {
      const url = new URL(
        `${config.tncApiUrl}/12h-user-registration-status-by-email`
      );
      url.searchParams.append('sportEventId', config.eventId);
      url.searchParams.append('email', debouncedEmail);
      const requestOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const response = await fetch(url, requestOptions);

      const result = await response.json();

      const { user, eventParticipant } = result.data;

      if (user) {
        setUserExist(true);
      }
      if (eventParticipant && eventParticipant.isCaptain) {
        setIsCaptain(true);
      }

      console.log('result', result);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setShowLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedEmail && !userExist) {
      userRegistrationStatusEmail(debouncedEmail);
    }
  }, [debouncedEmail, userExist]);

  const onPayment = async () => {
    setShowLoading(true);

    try {
      const formattedBirthdate = new Date(formData.birthdate)
        .toISOString()
        .split('T')[0];

      const payload = {
        ...formData,
        birthdate: formattedBirthdate,
      };

      const url = new URL(`${config.tncApiUrl}/event/12h/register`);
      url.searchParams.append('eventId', config.eventId);

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        setShowLoading(false);
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error en el registro del evento');
      }

      const result = await response.json();

      setShowLoading(false);
      loginStrava(result.data, formData.stravaId);
    } catch (error) {
      setShowLoading(false);
      console.error('Error en la solicitud de registro:', error);
      alert(error.message || 'Error al actualizar usuario');
    }
  };

  const populateFormData = () => {
    const stravaData = JSON.parse(localStorage.getItem('stravaAthlete'));
    const userRegistrationStatus = JSON.parse(
      localStorage.getItem('userRegistrationStatus')
    );

    const { refreshToken, stravaAthlete } = stravaData || {};
    const { user, eventParticipant } = userRegistrationStatus || {};
    let updatedFormData = { ...formData };

    if (stravaAthlete) {
      updatedFormData = {
        ...updatedFormData,
        firstName: stravaAthlete.firstname || updatedFormData.firstName || '',
        lastName: stravaAthlete.lastname || updatedFormData.lastName || '',
        stravaId: stravaAthlete.id || updatedFormData.stravaId || null,
        refreshToken: refreshToken || updatedFormData.refreshToken || null,
        photoUrl: stravaAthlete.profile || updatedFormData.photoUrl || null,
      };
    }

    if (eventParticipant && eventParticipant.isCaptain) {
      setIsCaptain(true);
    }

    if (user) {
      setUserExist(true);
      updatedFormData = {
        ...updatedFormData,
        firstName: user.name || updatedFormData.firstName || '',
        lastName: user.lastName || updatedFormData.lastName || '',
        email: user.email || updatedFormData.email || '',
        phone: user.phone || updatedFormData.phone || '',
        gender: user.gender || updatedFormData.gender || '',
        birthdate: user.birthdate
          ? new Date(user.birthdate).toISOString().split('T')[0]
          : '',

        identificationCard:
          user.identificationCard || updatedFormData.identificationCard || '',
        shoesBrand: user.shoesBrand || updatedFormData.shoesBrand || '',
        address: user.address || updatedFormData.address || '',
        countryId: user.countryId || updatedFormData.countryId || '',
        stateId: user.stateId || updatedFormData.stateId || '',
        cityId: user.cityId || updatedFormData.cityId || '',
        cityName: user.cityName || updatedFormData.cityName || '',
        countryName: user.countryName || updatedFormData.countryName || '',
        refreshToken: user.refreshToken || updatedFormData.refreshToken || null,
        stravaId: user.stravaId || updatedFormData.stravaId || null,
      };
    }

    if (updatedFormData.countryId) {
      fetchStates(updatedFormData.countryId);
    }
    if (updatedFormData.stateId) {
      fetchCities(updatedFormData.stateId);
    }

    setFormData(updatedFormData);
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (formData.refreshToken) {
      getAccesToken(formData.refreshToken);
    }
  }, [formData.refreshToken]);

  useEffect(() => {
    if (countries.length > 0) {
      populateFormData();
    }
  }, [countries]);

  function debounce(func, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  }

  const debouncedSetEmail = debounce((email) => setDebouncedEmail(email), 500);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: event.target.value });
    clearError(field);

    if (field === 'email' && !userExist) {
      console.log('value', value);
      debouncedSetEmail(value);
    }
  };

  const handleCountryChange = (event) => {
    const countryId = event.target.value;
    const selectedCountry = countries.find(
      (country) => country.id === countryId
    );

    setFormData({
      ...formData,
      countryId,
      countryName: selectedCountry ? selectedCountry.name : '',
      stateId: '',
      cityId: '',
    });
    setStates([]);
    setCities([]);

    clearError('countryId');
    fetchStates(countryId);
  };

  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setFormData({ ...formData, stateId, cityId: '' });
    setCities([]);
    clearError('stateId');
    fetchCities(stateId);
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    const selectedCity = cities.find((city) => city.id === cityId);
    setFormData({
      ...formData,
      cityId,
      cityName: selectedCity ? selectedCity.city : '',
    });
    clearError('cityId');
  };

  const handlePayment = () => {
    if (validateFields()) {
      onPayment({ ...formData });
    }
  };

  return (
    <div className='registration-page'>
      {showLoading && <Loading />}
      <Header signUp />
      <div className='container registration-page-container'>
        <Row>
          <Col xs={12} md={8} mdOffset={2}>
            <Typography
              variant='h4'
              align='center'
              style={{ marginTop: '20px', fontWeight: 'bold' }}
            >
              Registrarme
            </Typography>
          </Col>

          {/* Formulario dinámico */}
          <Col xs={12} mdOffset={2} md={8}>
            <div className='registration-form'>
              <Row>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Nombre */}
                  <TextField
                    label='Nombre'
                    fullWidth
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Apellido */}
                  <TextField
                    label='Apellido'
                    fullWidth
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Número de Identificación */}
                  <TextField
                    label='Cédula'
                    fullWidth
                    value={formData.identificationCard}
                    onChange={handleInputChange('identificationCard')}
                    error={!!errors.identificationCard}
                    helperText={errors.identificationCard}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Teléfono */}
                  <TextField
                    label='Teléfono'
                    type='tel'
                    fullWidth
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Email */}
                  <TextField
                    label='Email'
                    type='email'
                    fullWidth
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={userExist}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Fecha de Nacimiento */}
                  <TextField
                    label='Fecha de Nacimiento'
                    type='date'
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={formData.birthdate}
                    onChange={handleInputChange('birthdate')}
                    error={!!errors.birthdate}
                    helperText={errors.birthdate}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Género */}
                  <FormControl fullWidth>
                    <InputLabel>Género</InputLabel>
                    <Select
                      value={formData.gender}
                      onChange={handleInputChange('gender')}
                      style={{ textAlign: 'left' }}
                    >
                      {GENDERS.map((gender) => (
                        <MenuItem key={gender.value} value={gender.value}>
                          {gender.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.gender && (
                      <Typography color='error'>{errors.gender}</Typography>
                    )}
                  </FormControl>
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo País */}
                  <FormControl fullWidth>
                    <InputLabel>País</InputLabel>
                    <Select
                      value={formData.countryId}
                      onChange={handleCountryChange}
                      style={{ textAlign: 'left' }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.countryId && (
                      <Typography color='error'>{errors.countryId}</Typography>
                    )}
                  </FormControl>
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Estado */}
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={formData.stateId}
                      onChange={handleStateChange}
                      disabled={!states.length} // Deshabilitar si no hay estados
                      style={{ textAlign: 'left' }}
                    >
                      {states.map((state) => (
                        <MenuItem key={state.id} value={state.id}>
                          {state.state}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.stateId && (
                      <Typography color='error'>{errors.stateId}</Typography>
                    )}
                  </FormControl>
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Ciudad */}
                  <FormControl fullWidth>
                    <InputLabel>Ciudad</InputLabel>
                    <Select
                      value={formData.cityId}
                      onChange={handleCityChange}
                      disabled={!cities.length} // Deshabilitar si no hay ciudades
                      style={{ textAlign: 'left' }}
                    >
                      {cities.map((city) => (
                        <MenuItem key={city.id} value={city.id}>
                          {city.city}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.cityId && (
                      <Typography color='error'>{errors.cityId}</Typography>
                    )}
                  </FormControl>
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  {/* Campo Dirección */}
                  <TextField
                    label='Dirección'
                    fullWidth
                    value={formData.address}
                    onChange={handleInputChange('address')}
                    error={!!errors.address}
                    helperText={errors.address}
                  />
                </Col>
                <Col xs={12} md={6} className='field-container'>
                  <FormControl fullWidth>
                    <InputLabel>Marca de Zapatos</InputLabel>
                    <Select
                      value={formData.shoesBrand}
                      onChange={handleInputChange('shoesBrand')}
                      style={{ textAlign: 'left' }}
                    >
                      {SHOES.map((shoe) => (
                        <MenuItem key={shoe} value={shoe}>
                          {shoe}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.shoesBrand && (
                      <Typography color='error'>{errors.shoesBrand}</Typography>
                    )}
                  </FormControl>
                </Col>

                <Col xs={12}>
                  {userExist && isCaptain && (
                    <Typography
                      variant='body1'
                      style={{
                        marginTop: '10px',
                        marginBottom: '50px',
                        textAlign: 'justify',
                      }}
                    >
                      Capitanes recuerden que deben registrar a su equipo antes
                      del 31 de diciembre. En el siguiente link{' '}
                      <a
                        href='https://shorturl.at/lcz09'
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: '#ff4b03', fontWeight: 'bold' }}
                      >
                        https://shorturl.at/lcz09
                      </a>
                      .
                      <br />
                      <br />
                      Su equipo se verá reflejado 24hrs después, si ya
                      registraste tu equipo omite esta información.
                    </Typography>
                  )}
                </Col>
                <Col xs={12} md={6} mdOffset={3}>
                  <Button
                    fullWidth
                    onClick={handlePayment}
                    className='submit-button'
                  >
                    VER LISTADO DE PARTICIPANTES
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
      <Footer />
    </div>
  );
};

export default RegistrationPage;
